// Regra de cálculo do preço de venda (SPEC §6). Fonte única: o backend calcula e grava;
// o frontend usa o mesmo módulo só para recalcular em tempo real durante a edição.
// Percentuais entram e saem como número "cheio" (11 = 11%).

/**
 * Grupos de produto, na ordem de exibição (SPEC §5).
 * Banheiro e Cozinha deixou de ser grupo em 05/10/2026: os itens passaram para Rolos e Almofadinhas.
 */
export const GRUPOS = Object.freeze([
  Object.freeze({ codigo: 'ROLOS_ALMOFADINHAS', nome: 'Rolos e Almofadinhas' }),
  Object.freeze({ codigo: 'INSTITUCIONAL', nome: 'Institucional' }),
  Object.freeze({ codigo: 'BIOFREEZER', nome: 'Biofreezer' }),
  Object.freeze({ codigo: 'ALUMINIO', nome: 'Alumínio' }),
]);

export const CODIGOS_GRUPO = Object.freeze(GRUPOS.map((g) => g.codigo));

/** Grupos que já existiram: só para exibir rodadas antigas que ainda os tenham. */
const GRUPOS_ANTIGOS = Object.freeze({ BAN_COZ: 'Banheiro e Cozinha (grupo antigo)' });

/** Nome exibido do grupo; o próprio código quando desconhecido. */
export function nomeGrupo(codigo) {
  return GRUPOS.find((g) => g.codigo === codigo)?.nome ?? GRUPOS_ANTIGOS[codigo] ?? codigo;
}

/**
 * Grupos de uma lista de itens, na ordem oficial; grupos antigos/desconhecidos (rodadas antigas) no fim,
 * para nenhum item sumir da tela ou do PDF.
 */
export function gruposPresentes(codigos) {
  const presentes = new Set(codigos);
  const oficiais = GRUPOS.filter((g) => presentes.has(g.codigo));
  const outros = [...presentes].filter((c) => !GRUPOS.some((g) => g.codigo === c)).sort().map((c) => ({ codigo: c, nome: nomeGrupo(c) }));
  return [...oficiais, ...outros];
}

/**
 * Blocos dos parâmetros por grupo, na ordem da tela:
 * - custo:  valores em R$/kg somados ao custo do produto ANTES do cálculo (não recebem percentuais);
 * - venda:  percentuais que incidem sobre o preço de venda (%variáveis);
 * - margem: margem líquida (após o desconto máximo) e margem de negociação.
 */
export const SECOES_PARAMETROS = Object.freeze([
  Object.freeze({ chave: 'custo', titulo: 'Somado ao custo (R$/kg)', descricao: 'Entram no custo do produto antes dos percentuais.' }),
  Object.freeze({ chave: 'venda', titulo: 'Percentuais sobre o preço de venda', descricao: 'Calculados sobre o preço final.' }),
  Object.freeze({ chave: 'margem', titulo: 'Margens', descricao: null }),
]);

/** Campos dos parâmetros por grupo (SPEC §6.1), na ordem da tela. */
export const CAMPOS_PARAMETROS = Object.freeze([
  Object.freeze({ chave: 'freteKg', rotulo: 'Frete R$/kg', tipo: 'reais', secao: 'custo' }),
  Object.freeze({ chave: 'marketingKg', rotulo: 'Marketing R$/kg', tipo: 'reais', secao: 'custo' }),
  Object.freeze({ chave: 'trademarketingKg', rotulo: 'Trade marketing R$/kg', tipo: 'reais', secao: 'custo' }),
  Object.freeze({ chave: 'acordosKg', rotulo: 'Acordos R$/kg', tipo: 'reais', secao: 'custo', ajuda: 'Prêmios, bonificações e retornos financeiros' }),
  Object.freeze({ chave: 'fretePct', rotulo: 'Frete %', tipo: 'pct', secao: 'venda' }),
  Object.freeze({ chave: 'comissaoPct', rotulo: 'Comissão %', tipo: 'pct', secao: 'venda' }),
  Object.freeze({ chave: 'impostosPct', rotulo: 'Impostos (nota) %', tipo: 'pct', secao: 'venda' }),
  Object.freeze({ chave: 'encargosPct', rotulo: 'Encargos financeiros %', tipo: 'pct', secao: 'venda' }),
  Object.freeze({ chave: 'adicionaisPct', rotulo: 'Outros adicionais %', tipo: 'pct', secao: 'venda' }),
  Object.freeze({ chave: 'margemPct', rotulo: 'Margem líquida %', tipo: 'pct', secao: 'margem' }),
  Object.freeze({ chave: 'negociacaoPct', rotulo: 'Margem de negociação %', tipo: 'pct', secao: 'margem' }),
]);

/** Limites dos alertas visuais da prévia (SPEC §8.4). */
export const LIMITES_ALERTA = Object.freeze({
  variacaoMaxPct: 15,
  variacaoMinPct: -5,
  margemDescontoMinPct: 0,
});

/**
 * Arredondamento decimal "de planilha" (ARRED do Excel: metade para longe do zero),
 * sem o erro binário de 1.005 * 100 = 100.49999…
 */
export function arredondar(valor, casas = 2) {
  if (!Number.isFinite(valor)) return valor;
  const sinal = valor < 0 ? -1 : 1;
  const [mantissa, expoente] = Math.abs(valor).toExponential(14).split('e');
  const deslocado = Math.round(Number(`${mantissa}e${Number(expoente) + casas}`));
  return sinal * Number(`${deslocado}e-${casas}`);
}

/** Valores em R$/kg somados ao custo do produto (frete, marketing, trade marketing, acordos). */
export const CAMPOS_CUSTO_KG = Object.freeze(CAMPOS_PARAMETROS.filter((c) => c.secao === 'custo'));

/** Percentuais que incidem sobre o preço de venda (custos variáveis), na ordem da composição do preço. */
export const CAMPOS_VARIAVEIS = Object.freeze(CAMPOS_PARAMETROS.filter((c) => c.secao === 'venda'));

/**
 * Campos incluídos depois da primeira versão: parâmetros gravados antes deles (rodadas antigas,
 * últimos valores de uma tabela) valem zero nesses campos.
 */
const CAMPOS_NOVOS = Object.freeze(['marketingKg', 'trademarketingKg', 'acordosKg']);

/** Versão de 24/09/2026 (marketing, trade e acordos em %, sobre o venda), substituída em 30/09/2026. */
const CAMPOS_OBSOLETOS = Object.freeze(['marketingPct', 'trademarketingPct', 'acordosPct']);

/** Completa com zero os campos incluídos depois (ver CAMPOS_NOVOS) e remove os obsoletos. */
export function normalizarParametros(p) {
  if (!p) return p;
  const r = { ...p };
  for (const c of CAMPOS_OBSOLETOS) delete r[c];
  for (const c of CAMPOS_NOVOS) if (r[c] === undefined || r[c] === null) r[c] = 0;
  return r;
}

/** Soma dos valores em R$/kg acrescidos ao custo: frete + marketing + trade marketing + acordos. */
export function somaCustosKg(p) {
  return CAMPOS_CUSTO_KG.reduce((soma, c) => soma + (Number(p[c.chave]) || 0), 0);
}

/** %variáveis = frete% + comissão% + impostos% + encargos% + outros adicionais%. */
export function somaVariaveis(p) {
  return CAMPOS_VARIAVEIS.reduce((soma, c) => soma + (Number(p[c.chave]) || 0), 0);
}

/** Mensagem de bloqueio quando a combinação de percentuais é impossível; null quando está ok. */
export function validarPercentuais(pctVariaveis, margemPct, negociacaoPct) {
  if ([pctVariaveis, margemPct, negociacaoPct].some((v) => !Number.isFinite(v))) {
    return 'Preencha todos os percentuais.';
  }
  if (pctVariaveis < 0 || margemPct < 0 || negociacaoPct < 0) return 'Percentuais não podem ser negativos.';
  if (pctVariaveis + margemPct >= 100) {
    return `Custos variáveis (${formatarPct(pctVariaveis)}) + margem (${formatarPct(margemPct)}) chegam a 100% ou mais.`;
  }
  if (negociacaoPct >= 100) return 'A margem de negociação precisa ser menor que 100%.';
  return null;
}

/** Valida o bloco de parâmetros de um grupo. Retorna a primeira mensagem de erro ou null. */
export function validarParametros(parametros) {
  if (!parametros) return 'Parâmetros não informados.';
  const p = normalizarParametros(parametros);
  for (const campo of CAMPOS_PARAMETROS) {
    const v = p[campo.chave];
    if (v === null || v === undefined || v === '' || !Number.isFinite(Number(v))) return `Preencha "${campo.rotulo}".`;
    if (Number(v) < 0) return `"${campo.rotulo}" não pode ser negativo.`;
  }
  return validarPercentuais(somaVariaveis(p), Number(p.margemPct), Number(p.negociacaoPct));
}

function formatarPct(v) {
  return `${arredondar(v, 2).toString().replace('.', ',')}%`;
}

/**
 * Preço por kg de tabela:
 *   base     = custo_kg + custos_kg (frete + marketing + trade marketing + acordos, em R$/kg)
 *   preco_kg = base / (1 − %variáveis − %margem) / (1 − %negociação)
 */
export function calcularPrecoKg({ custoKg, custosKg, pctVariaveis, margemPct, negociacaoPct }) {
  const base = custoKg + custosKg;
  return base / (1 - (pctVariaveis + margemPct) / 100) / (1 - negociacaoPct / 100);
}

/**
 * Margens resultantes de um preço/kg:
 *   margem_cheia    = 1 − %variáveis − base / preco_kg
 *   margem_desconto = 1 − %variáveis − base / (preco_kg × (1 − %negociação))
 * Retorno em % (16.2 = 16,2%).
 */
export function calcularMargens({ base, pctVariaveis, negociacaoPct, precoKg }) {
  if (!(precoKg > 0)) return { margemCheia: null, margemDesconto: null };
  const v = pctVariaveis / 100;
  return {
    margemCheia: (1 - v - base / precoKg) * 100,
    margemDesconto: (1 - v - base / (precoKg * (1 - negociacaoPct / 100))) * 100,
  };
}

/**
 * Calcula um item da prévia.
 * - margemPct / negociacaoPct: valores do item (null/undefined = padrão do grupo).
 * - precoUnitManual: preço unitário digitado (mantido; só as margens são recalculadas).
 * - acrescimoUnit: R$ por unidade somado DEPOIS do markup (ex.: embalagem do Institucional);
 *   não recebe margem nem custos variáveis:
 *     preco_unit       = ARRED(preco_kg × peso + acréscimo, 2)
 *     preco_kg_efetivo = (preco_unit − acréscimo) / peso   → base das margens
 * - travarPreco + precoAtual: "manter preços atuais". Se o preço calculado for DIFERENTE do preço atual
 *   do CIGAM (para mais ou para menos), mantém o preço atual (situação 'mantido', não é publicado)
 *   e só recalcula as margens. Preço digitado à mão não é travado: vale o que foi digitado.
 * Retorna a situação ('atualizar' | 'mantido' | 'sem_custo' | 'sem_peso' | 'erro') e os valores calculados.
 * `precoCalculado` é sempre o preço da fórmula (ou o manual), antes da trava.
 */
export function calcularItem({ custoKg, peso, parametros, margemPct, negociacaoPct, precoUnitManual, acrescimoUnit, travarPreco, precoAtual }) {
  const margem = margemPct ?? Number(parametros.margemPct);
  const negociacao = negociacaoPct ?? Number(parametros.negociacaoPct);
  const vazio = {
    margemPct: margem,
    negociacaoPct: negociacao,
    precoKg: null,
    precoUnit: null,
    precoCalculado: null,
    precoKgEfetivo: null,
    margemCheia: null,
    margemDesconto: null,
  };

  if (custoKg === null || custoKg === undefined) {
    return { ...vazio, situacao: 'sem_custo', motivo: 'Não alterado: sem custo cadastrado.' };
  }
  if (!(peso > 0)) {
    return { ...vazio, situacao: 'sem_peso', motivo: 'Não alterado: sem peso no cadastro.' };
  }

  const pctVariaveis = somaVariaveis(parametros);
  const erro = validarPercentuais(pctVariaveis, margem, negociacao);
  if (erro) return { ...vazio, situacao: 'erro', motivo: erro };

  const custosKg = somaCustosKg(parametros);
  const base = custoKg + custosKg;
  const precoKg = calcularPrecoKg({ custoKg, custosKg, pctVariaveis, margemPct: margem, negociacaoPct: negociacao });
  const acrescimo = Number(acrescimoUnit) || 0;
  const manual = precoUnitManual !== null && precoUnitManual !== undefined;
  const precoCalculado = arredondar(manual ? Number(precoUnitManual) : precoKg * peso + acrescimo, 2);

  if (!(precoCalculado > 0)) {
    return { ...vazio, precoKg, situacao: 'erro', motivo: 'O preço unitário precisa ser maior que zero.' };
  }
  if (!(precoCalculado > acrescimo)) {
    return { ...vazio, precoKg, situacao: 'erro', motivo: 'O preço unitário precisa ser maior que o acréscimo por unidade.' };
  }

  // Manter preços atuais: qualquer diferença (para mais ou para menos) mantém o preço atual; só a margem muda.
  const atual = precoAtual > 0 ? arredondar(Number(precoAtual), 2) : null;
  const mantido = Boolean(travarPreco) && !manual && atual !== null && precoCalculado !== atual;
  const precoUnit = mantido ? atual : precoCalculado;

  const precoKgEfetivo = (precoUnit - acrescimo) / peso;
  const { margemCheia, margemDesconto } = calcularMargens({ base, pctVariaveis, negociacaoPct: negociacao, precoKg: precoKgEfetivo });
  return {
    margemPct: margem,
    negociacaoPct: negociacao,
    precoKg,
    precoUnit,
    precoCalculado,
    precoKgEfetivo,
    margemCheia,
    margemDesconto,
    situacao: mantido ? 'mantido' : 'atualizar',
    motivo: mantido ? `Preço mantido: o calculado ficou ${precoCalculado > atual ? 'acima' : 'abaixo'} do atual.` : null,
  };
}

/** Variação % do preço novo sobre o atual; null quando não há preço atual. */
export function variacaoPct(novo, atual) {
  if (novo === null || novo === undefined || !(atual > 0)) return null;
  return (novo / atual - 1) * 100;
}

/**
 * Composição do preço de UMA unidade e margem de contribuição, na tabela cheia e com o desconto máximo.
 *   receita = preço unitário − acréscimo (o acréscimo é repasse: entra e sai igual)
 *   MC      = receita − custo do produto − (frete + marketing + trade + acordos) R$/kg × peso − Σ(percentuais × receita)
 *   MC %    = MC / receita  (igual à "margem cheia" / "margem c/ desconto")
 * Retorna null quando não há preço, custo ou peso.
 */
export function composicaoPreco({ custoKg, peso, parametros, precoUnit, acrescimoUnit, negociacaoPct }) {
  if (custoKg === null || custoKg === undefined || !(peso > 0) || !(precoUnit > 0)) return null;
  const p = normalizarParametros(parametros);
  const acrescimo = Number(acrescimoUnit) || 0;
  const negociacao = negociacaoPct ?? Number(p.negociacaoPct);
  const custoProduto = custoKg * peso;
  // Valores por kg somados ao custo, já por unidade.
  const custosKg = CAMPOS_CUSTO_KG.map((c) => {
    const porKg = Number(p[c.chave]) || 0;
    return { chave: c.chave, rotulo: c.rotulo.replace(/ R\$\/kg$/, ''), porKg, valor: porKg * peso };
  });
  const custoTotal = custoProduto + custosKg.reduce((s, c) => s + c.valor, 0);

  function cenario(receita) {
    const variaveis = CAMPOS_VARIAVEIS.map((c) => {
      const pct = Number(p[c.chave]) || 0;
      return { chave: c.chave, rotulo: c.rotulo.replace(/ %$/, ''), pct, valor: (receita * pct) / 100 };
    });
    const totalVariaveis = variaveis.reduce((s, v) => s + v.valor, 0);
    const mc = receita - custoTotal - totalVariaveis;
    return { receita, variaveis, totalVariaveis, mc, mcPct: receita > 0 ? (mc / receita) * 100 : null, mcKg: mc / peso };
  }

  const receitaCheia = precoUnit - acrescimo;
  return {
    precoUnit,
    acrescimo,
    custoProduto,
    custosKg,
    custoTotal,
    negociacaoPct: negociacao,
    cheia: cenario(receitaCheia),
    desconto: cenario(receitaCheia * (1 - negociacao / 100)),
  };
}
